/**
 * Overview and Account.
 *
 * Overview is assembled from the SAME endpoints the detail views use, never from its own
 * summary query. A landing page with its own data source drifts from the pages it summarises,
 * and the drift is invisible: both look authoritative and only one is right.
 *
 * It deliberately does not compute a health score. A single green figure standing for a
 * platform where several instruments are still off would be the most misleading thing on the
 * screen, so this reports what is measured and what is not, side by side.
 */

import { Reading } from '../Reading';
import { VIEWS } from '../views-meta';
import { getAlerts, getCapacity, getIntegrations, getMe, getRouting } from '../api';
import { ProductionReading, verifiedProduction } from './Capacity';
import { usePoll } from '../usePoll';
import { Unavailable } from './Console';
import { central } from '../time';

export function Overview() {
  const alerts = usePoll(getAlerts, 20_000);
  const integrations = usePoll(getIntegrations, 30_000);
  const routing = usePoll(getRouting, 60_000);
  const capacity = usePoll(getCapacity, 60_000);

  const blind = VIEWS.filter((v) => v.coverage === 'none').length;
  const partial = VIEWS.filter((v) => v.coverage === 'partial').length;

  const a = alerts.data;
  const degraded = integrations.data?.integrations.filter((i) => i.state !== 'ok') ?? null;

  return (
    <>
      {a && !a.watchdogFiring && (
        <div className="notice red">
          <h3>The watchdog is not firing</h3>
          <p>
            Alert rules are not evaluating. Everything below that depends on them is unverified,
            including any figure reading zero.
          </p>
        </div>
      )}

      <div className="readings">
        {/* Firing is the one number an operator looks for first, so it goes first. */}
        {a ? (
          <Reading label="Alerts firing" value={String(a.firing.length)}
            state={alerts.failure ? 'stale' : 'live'} source="Prometheus"
            observedAt={new Date(a.observedAt)} environment="production"
            reason={a.firing.filter((f) => f.severity === 'critical').length > 0
              ? `${a.firing.filter((f) => f.severity === 'critical').length} critical`
              : 'the watchdog is excluded; it always fires'} />
        ) : (
          <Reading label="Alerts firing" state="unknown" source="operations API"
            reason={alerts.loading ? 'Reading.' : 'Alert state could not be read, so this is not zero.'} />
        )}

        {a ? (
          <Reading label="Rules loaded" value={String(a.rules.loaded)}
            state={alerts.failure ? 'stale' : 'live'} source="Prometheus"
            observedAt={new Date(a.observedAt)} environment="production"
            reason={`${a.rules.provisional} thresholds still provisional`} />
        ) : (
          <Reading label="Rules loaded" state="unknown" source="operations API" reason="Unavailable." />
        )}

        {degraded ? (
          <Reading label="Integrations degraded" value={String(degraded.length)}
            state={degraded.length > 0 ? 'error' : 'live'} source="live probe"
            observedAt={new Date(integrations.data!.observedAt)} environment="production"
            reason={degraded.length > 0 ? degraded.map((d) => d.name).join(', ') : 'all four responding'} />
        ) : (
          <Reading label="Integrations degraded" state="unknown" source="operations API" reason="Unavailable." />
        )}

        {routing.data ? (
          <Reading label="Operators reachable" value={String(routing.data.reachable)}
            state={routing.data.reachable === 0 ? 'error' : 'live'} source="operations database"
            observedAt={new Date(routing.data.observedAt)} environment="production"
            reason={`of ${routing.data.people.length} authorised`} />
        ) : (
          <Reading label="Operators reachable" state="unknown" source="operations API" reason="Unavailable." />
        )}

        {/* The same verified production floor the Capacity planner shows (G4), never a staging number. */}
        {capacity.data ? (
          <ProductionReading run={verifiedProduction(capacity.data.runs)} label="Production capacity" />
        ) : (
          <Reading label="Production capacity" state="unknown" source="operations API" reason="Unavailable." />
        )}

        <Reading label="Views not measured" value={String(blind)} unit={`of ${VIEWS.length}`}
          state="unknown" source="instrumentation gaps"
          reason={`${partial} more are partial. Each view says which change would fix it.`} />
      </div>

      <div className="notice">
        <h3>This page has no health score, on purpose</h3>
        <p>
          It reports what is measured and what is not, in the same place. Rolling those into one
          figure would hide the second half, and the second half is the part that decides whether
          the first can be trusted.
        </p>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ account */

export function Account() {
  const { data, failure, loading } = usePoll(getMe, 60_000);

  return (
    <>
      {loading && !data ? (
        <div className="readings">
          <Reading label="Account" state="unknown" source="loading" reason="Reading." />
        </div>
      ) : !data ? (
        failure ? <Unavailable what="Your account details" failure={failure} />
          : <div className="notice red"><h3>Your account details cannot be read</h3><p>The Operations API did not respond.</p></div>
      ) : (
        <div className="readings">
          <Reading label="Signed in as" value={data.username ?? `user ${data.peertubeUserId}`}
            state="live" source="PeerTube" observedAt={new Date(data.lastRoleCheck)} />
          <Reading label="Administrator"
            value={data.isAdmin === null ? 'Unknown' : data.isAdmin ? 'Yes' : 'No'}
            state={data.isAdmin === null ? 'unknown' : 'live'} source="PeerTube"
            observedAt={data.roleObservedAt ? new Date(data.roleObservedAt) : undefined}
            reason="An observation with a timestamp, not a standing fact." />
          <Reading label="Discord"
            value={data.discordLinked ? (data.discordUsername ?? 'linked') : 'Not linked'}
            state={data.discordLinked ? 'live' : 'unknown'} source="operations database"
            reason={data.discordLinked
              ? 'You can be mentioned when an alert matches your preferences.'
              : 'Until you link Discord you cannot be mentioned. Channel messages still post.'} />
          <Reading label="Session expires"
            value={central(data.sessionExpiresAt)}
            state="live" source="operations database"
            reason="Signing in again extends it; it is not renewed by activity." />
        </div>
      )}

      <div className="notice">
        <h3>Access is checked continuously, not once</h3>
        <p>
          Your administrator role is re-confirmed with PeerTube at least every 60 seconds, and
          before any sensitive change. If PeerTube cannot be reached, access is withheld rather
          than extended, so you see an unavailable state rather than a denial.
        </p>
      </div>
    </>
  );
}
